import previewModeBackgroundDrawer from '@core/app/actions/beambox/preview-mode-background-drawer';
import previewModeController from '@core/app/actions/beambox/preview-mode-controller';
import FnWrapper from '@core/app/actions/beambox/svgeditor-function-wrapper';
import { getWideAngleCameraData } from '@core/app/actions/camera/preview-helper/getWideAngleCameraData';
import handlePreviewSetupError from '@core/app/actions/camera/preview-helper/handlePreviewSetupError';
import tutorialController from '@core/app/components/tutorials/tutorialController';
import { PreviewMode } from '@core/app/constants/cameraConstants';
import tutorialConstants from '@core/app/constants/tutorial-constants';
import { setCameraPreviewState, useCameraPreviewStore } from '@core/app/stores/cameraPreview';
import { useCanvasStore } from '@core/app/stores/canvas/canvasStore';
import { setMouseMode } from '@core/app/stores/canvas/utils/mouseMode';
import showResizeAlert from '@core/helpers/device/fit-device-workarea-alert';
import getDevice from '@core/helpers/device/get-device';
import deviceMaster from '@core/helpers/device-master';
import versionChecker from '@core/helpers/version-checker';
import type { IDeviceInfo } from '@core/interfaces/IDevice';

export const endPreviewMode = (): void => {
  try {
    if (previewModeController.isPreviewMode || previewModeController.isStarting) {
      previewModeController.end();
    }
  } catch (error) {
    console.log(error);
  } finally {
    if (tutorialController.getNextStepRequirement() === tutorialConstants.END_PREVIEW_MODE) {
      tutorialController.handleNextStep();
    }

    // eslint-disable-next-line hooks/rules-of-hooks
    FnWrapper.useSelectTool();
  }
};

let isSettingUpPreview = false;

export const checkCameraOblique = async (device: IDeviceInfo): Promise<boolean> => {
  if (device.model === 'fhx2rf') return true;

  if (device.model === 'fbb2') {
    const vc = versionChecker(device.version);

    if (vc.meetRequirement('BB2_CAMERA_INSTALLATION')) {
      try {
        const res = await deviceMaster.getDeviceSetting('camera_installation');

        return res.status === 'ok' && res.value === '1';
      } catch (err) {
        console.warn('Failed to get camera installation setting', err);
      }
    }
  }

  return false;
};

export const getSupportedPreviewModes = (
  device: IDeviceInfo,
  { hasWideAngleCamera, isCameraOblique }: { hasWideAngleCamera: boolean; isCameraOblique: boolean },
): PreviewMode[] => {
  const modes = [PreviewMode.REGION];

  if (hasWideAngleCamera || device.model === 'fbm2') modes.push(PreviewMode.FULL_AREA);

  if (isCameraOblique) modes.push(PreviewMode.PRECISE_REGION);

  return modes;
};

export const handlePreviewClick = async ({ showModal = false }: { showModal?: boolean } = {}): Promise<boolean> => {
  if (tutorialController.getNextStepRequirement() === tutorialConstants.TO_PREVIEW_MODE) {
    tutorialController.handleNextStep();
  }

  if (previewModeController.isStarting || previewModeController.isDrawing) return false;

  if (previewModeController.isPreviewMode) {
    if (previewModeController.isFullArea) {
      previewModeController.previewFullWorkarea();

      return false;
    }

    setMouseMode('preview');

    return true;
  }

  const { device, isWorkareaMatched } = await getDevice(showModal);

  if (!device) return false;

  if (!isWorkareaMatched && !(await showResizeAlert(device!))) return false;

  const { canPreview, hasWideAngleCamera } = await getWideAngleCameraData(device);
  const isCameraOblique = await checkCameraOblique(device);

  setCameraPreviewState({
    supportedPreviewModes: getSupportedPreviewModes(device, { hasWideAngleCamera, isCameraOblique }),
  });

  if (device.model === 'ado1' || device.model === 'fbm2' || (hasWideAngleCamera && canPreview)) {
    setupPreviewMode();

    return false;
  }

  setMouseMode('pre_preview');

  return true;
};

export const setupPreviewMode = async ({
  callback,
  showModal,
}: { callback?: () => void; showModal?: boolean } = {}) => {
  if (isSettingUpPreview) return;

  isSettingUpPreview = true;

  const { device, isWorkareaMatched } = await getDevice(showModal);

  if (!(await previewModeController.checkDevice(device))) {
    isSettingUpPreview = false;

    return;
  }

  if (!isWorkareaMatched && !(await showResizeAlert(device!))) {
    isSettingUpPreview = false;

    return;
  }

  try {
    await previewModeController.start(device!);

    if (!previewModeController.isPreviewMode) {
      isSettingUpPreview = false;

      return;
    }

    const { pendingPreviewMode } = useCameraPreviewStore.getState();

    if (pendingPreviewMode && pendingPreviewMode !== previewModeController.previewManager?.previewMode) {
      await previewModeController.switchPreviewMode(pendingPreviewMode);
    }

    setCameraPreviewState({ pendingPreviewMode: undefined });

    if (previewModeController.isFullArea) {
      previewModeController.previewFullWorkarea().then(() => {
        if (tutorialController.getNextStepRequirement() === tutorialConstants.PREVIEW_PLATFORM) {
          tutorialController.handleNextStep();
        }
      });
    }

    callback?.();
  } catch (error) {
    console.error(error);
    handlePreviewSetupError(device!, error);

    // eslint-disable-next-line hooks/rules-of-hooks
    FnWrapper.useSelectTool();
  } finally {
    isSettingUpPreview = false;
  }
};

useCanvasStore.subscribe(
  (state) => state.mouseMode,
  (mouseMode) => {
    if (mouseMode !== 'preview') {
      if (previewModeController.isPreviewMode) {
        previewModeBackgroundDrawer.clearBoundary();
      }
    } else if (previewModeController.isPreviewMode) {
      previewModeBackgroundDrawer.resetBoundary();
    }
  },
);
